import { useEffect, useRef, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Input, Loader, EmptyState, Modal } from "@/components/ui/Primitives";
import { unwrap } from "@/lib/unwrap";
import { MessageCircle, Send, Plus, Users2 } from "lucide-react";
import { toast } from "sonner";
import CopilotAssistBar from "@/components/CopilotAssistBar";
import ReplyCopilotButton from "@/components/ReplyCopilotButton";

export default function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);

  // Most recent message from the OTHER party — surfaces the reply copilot.
  const lastIncoming = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      const mine = m.sender_user_id === user?.user_id || m.from === "me";
      if (!mine) return m.body || "";
    }
    return "";
  })();

  const load = async () => {
    setLoading(true);
    try {
      const [c, k] = await Promise.all([
        api.get("/conversations").catch(() => ({ data: [] })),
        api.get("/me/chat-contacts").catch(() => ({ data: [] })),
      ]);
      const list = unwrap(c.data, "conversations");
      setConvs(list);
      setContacts(unwrap(k.data, "contacts"));
      if (list.length && !active) setActive(list[0]);
    } finally { setLoading(false); }
  };

  const loadMessages = async (convId) => {
    try {
      const { data } = await api.get(`/conversations/${convId}/messages`);
      setMessages(unwrap(data, "messages"));
      await api.post(`/conversations/${convId}/read`).catch(() => {});
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (active?.conversation_id) loadMessages(active.conversation_id); }, [active?.conversation_id]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  // Live updates — poll every 4s while a conversation is open and tab is visible.
  useEffect(() => {
    if (!active?.conversation_id) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const { data } = await api.get(`/conversations/${active.conversation_id}/messages`);
        if (cancelled) return;
        const next = unwrap(data, "messages");
        setMessages((prev) => (next.length !== prev.length ? next : prev));
      } catch {}
    };
    const id = setInterval(tick, 4000);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [active?.conversation_id]);

  // Poll conversation list every 15s for new threads / last-message preview updates.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      api.get("/conversations").then(({ data }) => setConvs(unwrap(data, "conversations"))).catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${active.conversation_id}/messages`, { body });
      setMessages((m) => [...m, data]);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const startChat = async (contact) => {
    try {
      const { data } = await api.post("/conversations", {
        participant_user_id: contact.user_id,
        lease_id: contact.lease_id || null,
      });
      setOpen(false);
      await load();
      setActive(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start conversation");
    }
  };

  if (loading) return <Loader/>;

  const otherName = (c) => {
    if (!c) return "—";
    const other = (c.participants || []).find((p) => p.user_id !== user?.user_id) || c.other_user || {};
    return other.name || other.email || c.title || "Conversation";
  };

  return (
    <div>
      <PageHeader
        title="Messages"
        subtitle={user?.role === "landlord" ? "Talk to your tenants in real time." : "Chat directly with your landlord."}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-conversation-button"><Plus className="w-4 h-4"/> New chat</Button>}
      />

      {convs.length === 0 ? (
        <EmptyState
          icon={MessageCircle}
          title="No conversations yet"
          description={user?.role === "landlord" ? "Start a chat with a tenant from one of your leases." : "Open a thread with your landlord."}
          action={null}
        />
      ) : (
        <div className="grid lg:grid-cols-[320px_1fr] gap-5 h-[70vh] min-h-[520px]">
          <Card className="p-0 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Conversations</div>
            <div className="flex-1 overflow-y-auto">
              {convs.map((c) => (
                <button
                  key={c.conversation_id}
                  data-testid={`conv-row-${c.conversation_id}`}
                  onClick={() => setActive(c)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${active?.conversation_id === c.conversation_id ? "bg-green-50" : "hover:bg-slate-50"}`}
                >
                  <div className="font-semibold text-sm text-slate-900 truncate">{otherName(c)}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message?.body || c.subject || "No messages yet"}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(c.last_message?.created_at || c.updated_at || c.created_at)}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-0 flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-none">
              <div className="w-9 h-9 rounded-full bg-green-800 text-white flex items-center justify-center text-sm font-semibold">
                {(otherName(active) || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{otherName(active)}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60"/>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"/>
                  </span>
                  Live · auto-refresh every 4s
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/60">
              {messages.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-10">Say hello — your message starts the thread.</div>
              ) : messages.map((m, i) => {
                const mine = m.sender_user_id === user?.user_id || m.from === "me";
                return (
                  <div key={m.message_id || i} data-testid={`message-${i}`}
                    className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${mine ? "ml-auto bg-green-800 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"}`}>
                    <div>{m.body}</div>
                    <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>{formatDateTime(m.created_at)}</div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={send} className="border-t border-slate-100 p-3 flex flex-col gap-2 flex-none">
              {/* When the landlord has an incoming message from the tenant,
                  surface a one-click "Draft reply" copilot. */}
              {user?.role === "landlord" && !!lastIncoming && (
                <div className="flex items-center gap-2 -mb-1">
                  <ReplyCopilotButton
                    channel="message"
                    incoming={lastIncoming}
                    threadContext={messages.slice(-6).map(m => `${m.sender_user_id === user?.user_id ? "Me" : otherName(active)}: ${m.body}`).join("\n")}
                    onDraft={({ body }) => setDraft(body || "")}
                    testid="msg-reply-copilot"
                  />
                  <span className="text-[10px] text-slate-400">Copilot drafts replies for you</span>
                </div>
              )}
              {draft.trim().length > 10 && (
                <CopilotAssistBar text={draft} onReplace={setDraft} />
              )}
              <div className="flex items-center gap-2">
                <input
                  data-testid="message-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:bg-white focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none transition-all"
                />
                <Button type="submit" disabled={sending || !draft.trim()} data-testid="message-send" className="rounded-full !px-4">
                  <Send className="w-4 h-4"/>
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Start a conversation" testid="new-chat-modal">
        {contacts.length === 0 ? (
          <div className="py-6 text-center">
            <Users2 className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
            <div className="text-sm text-slate-500">No contacts available yet. {user?.role === "landlord" ? "Create a lease with a tenant first." : "Your landlord will appear here once a lease is in place."}</div>
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <button key={c.user_id} onClick={() => startChat(c)} data-testid={`contact-${c.user_id}`}
                className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-green-800 hover:bg-green-50 transition-colors">
                <div className="font-semibold text-sm text-slate-900">{c.name || c.email}</div>
                <div className="text-xs text-slate-500">{c.email} {c.lease_id ? `· Lease ${c.lease_id.slice(-6)}` : ""}</div>
              </button>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
